import { useForm } from "react-hook-form";
import { Button, Input, Modal } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import type { InvoiceDTO } from "@wmart/shared-types";

interface MarkPaidFormValues {
  amountMinor: string; // string for input field; coerced to int on submit
  reference: string;
  receivedAt: string; // datetime-local string; converted to ISO on submit
}

interface Props {
  invoice: InvoiceDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

const REF_MAX = 200;

/**
 * MarkPaidForm — modal for manual bank-transfer reconciliation
 * (POST /v1/admin/invoices/:id/mark-paid).
 *
 * V1.5 contract: amount MUST equal `invoice.totalMinor` (no partials),
 * currency defaults to GBP, reference is a free-form 1-200 char operator
 * string (typically the bank transaction ref). The form pre-fills the
 * amount so operators just confirm-and-paste-ref.
 *
 * `receivedAt` is optional — server clock fallback. Surfaced as a
 * datetime-local input so the operator can backdate a payment that hit
 * the bank yesterday.
 *
 * 4xx errors surface via the toast provider (`amount_mismatch`,
 * `already_paid`, etc. carry their detail strings).
 */
export function MarkPaidForm({ invoice, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<MarkPaidFormValues>({
    defaultValues: {
      amountMinor: String(invoice.totalMinor),
      reference: "",
      receivedAt: "",
    },
  });

  const amountStr = watch("amountMinor");
  const reference = watch("reference");

  async function onSubmit(values: MarkPaidFormValues) {
    const amount = Number.parseInt(values.amountMinor, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      notify("error", "Amount must be a positive integer (pence)");
      return;
    }
    const body: {
      amountMinor: number;
      currency: string;
      reference: string;
      receivedAt?: string;
    } = {
      amountMinor: amount,
      currency: "GBP",
      reference: values.reference.trim(),
    };
    if (values.receivedAt) {
      // datetime-local → ISO. The browser supplies local-time without a
      // TZ; new Date(...).toISOString() converts to UTC consistently.
      body.receivedAt = new Date(values.receivedAt).toISOString();
    }
    try {
      await api.post(`/v1/admin/invoices/${invoice.id}/mark-paid`, body);
      notify("success", `Invoice ${invoice.number} marked paid`);
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Mark paid failed");
    }
  }

  const parsedAmount = Number.parseInt(amountStr, 10);
  const amountMatches =
    Number.isFinite(parsedAmount) && parsedAmount === invoice.totalMinor;
  const submitDisabled =
    isSubmitting || !amountMatches || reference.trim().length === 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Mark invoice ${invoice.number} paid`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={submitDisabled}
            loading={isSubmitting}
          >
            Reconcile payment
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <Input
          label="Amount (pence)"
          type="number"
          inputMode="numeric"
          helper={`Must match invoice total ${formatGBP(invoice.totalMinor)} (${invoice.totalMinor} pence). Partial payments are not supported in V1.5.`}
          error={!amountMatches ? "Amount must equal invoice total" : undefined}
          {...register("amountMinor", { required: true })}
        />
        <Input
          label="Bank reference"
          placeholder="e.g. FT26010001234"
          maxLength={REF_MAX}
          helper={`${reference.length}/${REF_MAX} — operator-supplied bank/AR ref`}
          error={errors.reference?.message}
          {...register("reference", {
            required: "Reference is required",
            minLength: { value: 1, message: "Reference is required" },
            maxLength: {
              value: REF_MAX,
              message: `Max ${REF_MAX} characters`,
            },
          })}
        />
        <Input
          label="Received at (optional)"
          type="datetime-local"
          helper="Defaults to now. Set to the date the funds hit the account."
          {...register("receivedAt")}
        />
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
